'use client';
import React, { useRef } from 'react';
import { m, useInView } from 'framer-motion';
import { PortableText } from '@portabletext/react';
import { PortableTextBlock } from 'sanity';
import { components } from './PortableTextComponents';
import { fadeUp, fadeUpLeft } from '@/assets/framer/animations';
import { sectionPadding } from '../utils/utils';
import Image from 'next/image';

import { useRelativeCoordinates } from '@/hooks/useRelativeCoordinates';
import { ArrowRight } from '@phosphor-icons/react/dist/ssr';

function SVGPatterns({
  mousePosition,
  isInView,
}: {
  className?: string;
  mousePosition: any;
  isInView: boolean;
}) {
  return (
    <div>
      <m.svg
        initial='hidden'
        variants={fadeUpLeft}
        transition={{
          delay: mousePosition.x ? 0 : 0.3,
          type: 'tween',
        }}
        width='148'
        height='150'
        viewBox='0 0 148 150'
        fill='none'
        xmlns='http://www.w3.org/2000/svg'
        className='absolute -top-24 right-[35%] z-0 sm:-top-28 sm:right-[33%]'
        animate={{
          ...(isInView
            ? {
                opacity: 1,
                x: mousePosition.x ? mousePosition.x / 96 + 96 : 96,
                y: mousePosition.y ? mousePosition.y / 50 - 10 : 0,
              }
            : fadeUp.hidden),
        }}
      >
        <path
          d='M26.7794 80.5397L14.7943 80.5164L14.8165 69.1245L26.8016 69.1479L26.7794 80.5397ZM39.8543 68.2676L28.3116 68.2451L28.335 56.2293L39.8777 56.2518L39.8543 68.2676ZM53.2535 54.6677L41.1678 54.6441L41.1916 42.4472L53.2772 42.4707L53.2535 54.6677ZM66.7118 41.6717L54.3648 41.6476L54.3905 28.4242L66.7376 28.4483L66.7118 41.6717ZM79.7097 27.6282L67.624 27.6047L67.6491 14.7435L79.7348 14.7671L79.7097 27.6282ZM93.3973 41.4419L81.3117 41.4184L81.3369 28.4767L93.4225 28.5003L93.3973 41.4419ZM106.905 54.4904L94.8198 54.4669L94.8436 42.2699L106.929 42.2935L106.905 54.4904ZM120.192 67.8606L108.106 67.837L108.129 56.0829L120.215 56.1064L120.192 67.8606ZM108.44 82.47L120.525 82.4936L120.503 93.8855L108.417 93.8619L108.44 82.47ZM95.1019 95.4461L107.188 95.4697L107.164 107.667L95.0782 107.643L95.1019 95.4461ZM67.8016 122.222L79.8873 122.246L79.8622 135.107L67.7766 135.083L67.8016 122.222ZM54.5972 108.128L66.9442 108.152L66.9184 121.375L54.5714 121.351L54.5972 108.128ZM41.451 95.08L53.5366 95.1035L53.5129 107.3L41.4272 107.277L41.451 95.08ZM52.8613 80.4698L41.3991 80.4475L41.4209 69.2569L52.8831 69.2792L52.8613 80.4698ZM66.5398 68.0177L54.4541 67.9941L54.4771 56.24L66.5627 56.2635L66.5398 68.0177ZM93.5063 68.0702L81.1592 68.0462L81.1826 56.0304L93.5297 56.0544L93.5063 68.0702ZM81.471 93.8094L81.4927 82.6792L93.6186 82.7028C93.6186 82.7028 93.7594 82.6427 93.8399 82.6026L93.8181 93.8335L81.471 93.8094ZM79.5463 80.5218L67.4606 80.4983L67.4824 69.3077L79.5681 69.3312L79.5463 80.5218ZM54.7876 82.6272L66.8733 82.6507L66.8521 93.5193L54.7665 93.4957L54.7876 82.6272ZM68.136 95.132L80.483 95.156L80.4587 107.615L68.1117 107.591L68.136 95.132ZM106.512 80.836L94.3864 80.8124C94.3864 80.8124 94.2455 80.8725 94.165 80.9126L94.188 69.098L106.535 69.1221L106.512 80.8561L106.512 80.836ZM80.1999 54.7001L67.8528 54.676L67.8771 42.2174L80.2242 42.2415L80.1999 54.7001ZM28.625 82.858L40.1676 82.8805L40.147 93.4874L28.6043 93.4649L28.625 82.858ZM93.6034 121.427L81.5178 121.404L81.543 108.462L93.6287 108.486L93.6034 121.427ZM133.197 80.9081L121.112 80.8846L121.134 69.1506L133.22 69.1741L133.197 80.9081ZM0.701298 68.3523C0.39966 68.3517 0.157839 68.5928 0.157251 68.8947L0.130855 82.4401C0.130267 82.742 0.371146 82.984 0.672785 82.9846L13.9851 83.0106L13.9625 94.5836C13.9619 94.8855 14.2028 95.1275 14.5044 95.1281L26.7912 95.152L26.7671 107.53C26.7665 107.832 27.0073 108.074 27.3089 108.075L39.9375 108.099L39.9117 121.343C39.9111 121.645 40.1519 121.887 40.4536 121.887L53.1426 121.912L53.1163 135.417C53.1157 135.719 53.3565 135.961 53.6581 135.962L66.709 135.987L66.6833 149.15C66.6828 149.452 66.9236 149.694 67.2253 149.695L80.7587 149.721C81.0604 149.722 81.3022 149.481 81.3028 149.179L81.3284 136.016L93.8967 136.04C94.1984 136.041 94.4401 135.8 94.4407 135.498L94.4665 122.254L107.638 122.28C107.94 122.28 108.182 122.039 108.182 121.737L108.208 108.494L121.198 108.519C121.5 108.52 121.742 108.279 121.742 107.977L121.767 95.4981L134.536 95.523C134.838 95.5236 135.08 95.2825 135.08 94.9806L135.104 82.522L147.23 82.5456C147.532 82.5462 147.774 82.3052 147.774 82.0033L147.801 68.4578C147.801 68.1559 147.56 67.9139 147.259 67.9133L134.771 67.889L134.796 55.0882C134.796 54.7863 134.556 54.5443 134.254 54.5437L121.485 54.5189L121.509 42.0401C121.51 41.7382 121.269 41.4962 120.967 41.4956L107.976 41.4703L108.002 28.2268C108.003 27.9249 107.762 27.6829 107.46 27.6823L94.2889 27.6566L94.3147 14.4332C94.3153 14.1313 94.0744 13.8893 93.7727 13.8887L81.2045 13.8642L81.2301 0.701151C81.2307 0.399246 80.9899 0.157255 80.6883 0.156667L67.1547 0.130295C66.8531 0.129707 66.6113 0.370758 66.6107 0.672662L66.5851 13.8357L53.5342 13.8103C53.2326 13.8097 52.9907 14.0507 52.9902 14.3527L52.9638 27.8579L40.2749 27.8331C39.9733 27.8325 39.7315 28.0736 39.7309 28.3755L39.7051 41.6191L27.0765 41.5945C26.7749 41.5939 26.5331 41.8349 26.5325 42.1368L26.5084 54.5149L14.2216 54.491C13.92 54.4904 13.6782 54.7314 13.6777 55.0334L13.6517 68.3775L0.701298 68.3523Z'
          fill='#51504D'
        />
      </m.svg>
      <m.svg
        initial='hidden'
        variants={fadeUpLeft}
        transition={{
          delay: mousePosition.x ? 0 : 0.3,
          type: 'tween',
        }}
        xmlns='http://www.w3.org/2000/svg'
        width='41'
        height='40'
        viewBox='0 0 41 40'
        fill='none'
        className='absolute right-[34%] top-12 z-0 sm:right-[31%]'
        animate={{
          ...(isInView
            ? {
                opacity: 1,
                x: mousePosition.x ? mousePosition.x / 50 - 10 : 0,
                y: mousePosition.y ? mousePosition.y / 50 - 10 : 0,
              }
            : fadeUp.hidden),
        }}
      >
        <path
          d='M26.2136 25.4736H14.043V14.0564H26.2136V25.4736ZM0.515865 27.2331H13.1641V38.9754C13.1641 39.2623 13.3934 39.4918 13.68 39.4918H26.653C26.9396 39.4918 27.1689 39.2623 27.1689 38.9754V26.296H39.6834C39.97 26.296 40.1992 26.0665 40.1992 25.7796V12.7751C40.1992 12.4882 39.97 12.2587 39.6834 12.2587H27.0351V0.516358C27.0351 0.229492 26.8059 0 26.5193 0H13.5462C13.2596 0 13.0304 0.229492 13.0304 0.516358V13.1958H0.515865C0.229273 13.1958 0 13.4253 0 13.7122V26.6976C0 26.9845 0.229273 27.214 0.515865 27.214'
          fill='#51504D'
        />
      </m.svg>
    </div>
  );
}
export default function TwoColumnTextImage(props: {
  data?: {
    sectionId?: string;
    heading?: PortableTextBlock;
    subHeading?: PortableTextBlock;
    image: {
      url: string;
      alt?: string;
      blurDataURL: string;
    };
    mobileImage: {
      url: string;
      alt?: string;
      blurDataURL: string;
    };
    displayOptions: {
      padding?: 'py' | 'pt' | 'pb' | 'p-0' | undefined;
    };
  };
}) {
  const sectionId = props.data?.sectionId;
  const heading = props.data?.heading;
  const subHeading = props.data?.subHeading;
  const image = props.data?.image;
  const mobileImage = props.data?.mobileImage;
  const section = useRef(null);

  const isInView = useInView(section, { amount: 0.4, once: true });

  const displayOptions = props.data?.displayOptions;

  const { coordinates: mousePosition, handleMouseMove } =
    useRelativeCoordinates(section);

  return (
    <>
      <section
        id={sectionId}
        ref={section}
        className={`${sectionPadding(displayOptions?.padding)} relative -mx-4 w-[auto] scroll-m-16 overflow-hidden rounded-xl bg-[radial-gradient(265.8%_154.63%_at_65.01%_111.08%,_#D3B203_0%,_#1D1D1B_39.04%)] sm:m-0 sm:rounded-xl [&_a]:text-[#8BD0D5]`}
        onMouseMove={(e) => handleMouseMove(e)}
      >
        {' '}
        <SVGPatterns mousePosition={mousePosition} isInView={isInView} />
        <m.div
          className={`container relative mx-0 flex w-full justify-between py-6 sm:mx-auto sm:py-4`}
          variants={fadeUp}
          initial='hidden'
          animate={isInView ? 'visible' : 'hidden'}
        >
          <div className='relative flex w-[65%] flex-col justify-center gap-3 overflow-hidden pl-7 sm:gap-2 sm:pl-12'>
            <m.div
              initial='hidden'
              animate={isInView ? 'visible' : 'hidden'}
              variants={fadeUp}
              className='group relative z-10 flex max-w-sm items-center font-display text-[32px] leading-8 tracking-tighter text-charcoal sm:max-w-3xl sm:gap-4 sm:text-2xl'
            >
              {heading && (
                <PortableText
                  components={components}
                  value={heading}
                ></PortableText>
              )}
              <ArrowRight
                weight='bold'
                className='hidden h-10 w-10 text-lg font-semibold text-white transition-transform duration-500 group-hover:translate-x-2 sm:block sm:h-6 sm:w-6'
              />
            </m.div>
            <m.div
              initial='hidden'
              animate={isInView ? 'visible' : 'hidden'}
              variants={fadeUp}
              className='relative z-10 max-w-sm font-sans text-charcoal sm:max-w-3xl sm:text-base'
            >
              {subHeading && (
                <PortableText
                  components={components}
                  value={subHeading}
                ></PortableText>
              )}
            </m.div>
          </div>

          {image?.url && (
            <div className='hidden w-[35%] pr-2 sm:block sm:pr-4'>
              <Image
                className='z-[5] !m-0 h-[180px] w-full'
                src={image?.url}
                alt={image?.alt || 'Image'}
                width={300}
                height={100}
              />
            </div>
          )}
          {mobileImage?.url && (
            <div className='block w-[35%] pr-2 sm:hidden sm:pr-4'>
              <Image
                className='z-[5] !m-0 h-[180px] w-full'
                src={mobileImage?.url}
                alt={mobileImage?.alt || 'Image'}
                width={300}
                height={100}
              />
            </div>
          )}
        </m.div>
      </section>
    </>
  );
}
